/**
 * PF Candle Home V2 — P.F. Candle Co.'s own BrandDataset + local mock data.
 *
 * Holds the brand datasets for the three `brand` maturity variants plus the
 * shared style/copy constants used across the sections. Product catalog data
 * for the established/mid brands is read from the playground entity layer in
 * `Page.tsx` (canonical mock source); the `new` brand ships a tiny inline
 * starter set here since a brand-new storefront has no catalog yet.
 *
 * This is ONE brand's instance data — the reusable `BrandDataset` type + the
 * rendering framework it feeds live at
 * `src/components/shared/brand-home-framework/`, not here. See that
 * package's SKILL.md for how to plug in a different brand.
 */
import {
  type BrandDataset,
  type BrandKey,
  type BrandVideo,
  type CategoryTile,
  type CollectionEntry,
  type StarterProduct,
} from "@faire/prototype-playground/components/shared/brand-home-framework/types";

export type {
  BrandDataset,
  BrandKey,
  BrandVideo,
  CategoryTile,
  CollectionEntry,
  StarterProduct,
};

// P.F. Candle Co. brand (real entity token — used for the established/mid data).
export const PF_BRAND_TOKEN = "b_a9b3b023";

// The brand's own "Register on Faire" router-entry surface — passed to the
// shared Masthead's `entryRoute` prop so its "..." menu keeps a nav aid into
// that flow. Most brands piped through the framework won't have one of these.
export const PF_ENTRY_ROUTE = "/rl/pfcandle-home-v2-entry";

// Brand color block — P.F. Candle Co.'s dark brown, pulled from their live site.
export const MAROON = "rgb(70, 44, 8)";
export const MAROON_DEEP = "rgb(48, 30, 5)";
// Brand SECONDARY: a light surface tint that carries brand hue while staying as
// light as the neutral tertiary surface — so contrast is consistent across brands.
// Primary + secondary is the whole brand-identity profile.
export const MAROON_SECONDARY = "rgb(243, 237, 228)";
export const CREAM = "#fffdfa";

// Shared responsive horizontal padding for padded (non-full-bleed) sections.
// Re-exported from the framework so existing local imports keep working.
export { PADX } from "@faire/prototype-playground/components/shared/brand-home-framework/constants";

// LiveToolbar variant/state selections, persisted so they survive navigation
// (entry/router surface ↔ Faire home) and reloads. Shared by Page.tsx and
// EntryPage.tsx so the "Faire landing" toggle set on the router surface is the
// one honored when the shopper clicks into Faire.
export const TOOLBAR_SETTINGS_KEY = "rl-pfcandle-home-v2:toolbar-settings";
export interface ToolbarSettings {
  /** Plan tier: 1 = Paid (personalized), 2 = Free (generic). */
  plan?: number;
  option?: number;
  tabVariant?: number;
  brand?: number;
  auth?: number;
  /** 1 = FD homepage, 2 = all-products grid. */
  landing?: number;
  /** 1 = Faire Direct (product-heavy), 2 = Marketplace (brand storytelling). */
  source?: number;
  /** Grid gutter width: 1 = 16px, 2 = 20px, 3 = 24px (see GUTTER_PX in Page). */
  gutter?: number;
  /** Brand-name treatment: 1 = wordmark, 2 = sans, 3 = serif (see Page). */
  nameStyle?: number;
  /** Category tiles module display toggle: 1 = shown, 2 = hidden. */
  categories?: number;
}
export const readToolbarSettings = (): ToolbarSettings => {
  try {
    return JSON.parse(localStorage.getItem(TOOLBAR_SETTINGS_KEY) ?? "{}");
  } catch {
    return {};
  }
};
export const writeToolbarSettings = (patch: ToolbarSettings): void => {
  try {
    localStorage.setItem(
      TOOLBAR_SETTINGS_KEY,
      JSON.stringify({ ...readToolbarSettings(), ...patch })
    );
  } catch {
    /* localStorage unavailable — ignore */
  }
};

// Live production "Summer Archive" cover, cropped tall so the candles aren't cut.
export const HERO_COVER =
  "https://cdn.faire.com/fastly/532bf8733b6371b685ddd2ba2e466fcd1cb08ae4c6278f36f18a6d7e0d7f4306.png?fit=crop&format=jpg&width=1200&height=680";

// The brand's real production cover banner (the "2026 Seasonals" header image
// off the live Faire brand page) — used by the current production header (option 2).
export const COVER_BANNER =
  "https://cdn.faire.com/fastly/c8956523aa69023c5251f8319de6051c1e5f8d461dd3b7e735f8375509149630.png?dpr=2&format=jpg&precrop=1652,375,x202,y45,safe";

// The brand's real "Tried & True Best Sellers" collection hero (off the live
// Faire brand page). Hand-set for now — collection images aren't yet auto-pulled
// with the brand token (see the featured-collection module).
export const PROMO_IMAGE =
  "https://cdn.faire.com/fastly/65fb58660b8b1232c671a8f525c90d76a91cf2fb93231142ab17827768ceb4c5.jpeg?bg-color=FFFFFF&dpr=1&fit=crop&format=jpg&height=720&width=720";

// Candles featured-collection lifestyle image (brand-provided retail shot).
export const CANDLES_LIFESTYLE_IMAGE =
  "https://pfcandleco.com/cdn/shop/files/SECONDARY-RETAIL-DESKTOP-2.jpg?v=1759330176";

// "Fall Classics" featured-collection image (brand-provided).
export const FALL_CLASSICS_IMAGE =
  "https://cdn.faire.com/fastly/56495d0c663004bb36882f9237402b5f48a2d5d99691559e13c80454e38a8c7f.jpeg?bg-color=FFFFFF&dpr=1&fit=crop&format=jpg&height=720&width=720";

// Brand videos — stubbed from the P.F. Candle Co. About page (3 videos). One
// card per video; the module falls back to a 3-up editorial row for now.
export const BRAND_VIDEOS: BrandVideo[] = [
  {
    title: "Factory Friday: The HI-FI Edition",
    thumbnail:
      "https://cdn.faire.com/fastly/4f5cc1d029fdfd6d4dff58d2d33020d6639bded1904dc90855e09cab353caf16.jpeg?dpr=2&format=jpg&height=360",
    duration: "1 min",
    date: "Jul 12, 2026",
    description:
      "Come with us to check out the behind the scenes process of producing our new premium line, HI-FI. 🍒 Made by our team in our California factory, with love.",
    tags: ["Behind the Scenes"],
  },
  {
    title: "Introducing: Factory Fridays at P.F. Candle Co.",
    thumbnail:
      "https://cdn.faire.com/fastly/322b557f32b0be6a132c5f26c2b013270f7de399e892b3e6f76390287c581464.jpeg?dpr=2&format=jpg&height=360",
    duration: "1 min",
    date: "Jul 12, 2026",
    description:
      "Welcome to Factory Fridays where we take you behind the scenes of our home fragrance development and production process. Made in Los Angeles with love 🤎🕯️🌴",
    tags: ["Behind the Scenes", "Product Close-Up"],
  },
  {
    title: "New! Our HI-FI Premium Collection",
    thumbnail:
      "https://cdn.faire.com/fastly/eb5867cf46c8d758f709a99fde3908c0ff8c381a9bf5e931e76851896ce071ce.jpeg?dpr=2&format=jpg&height=360",
    duration: "3 min",
    date: "Jul 12, 2026",
    description:
      "Our newest collection, the first premium offering from P.F. Candle Co., inspired by listening bars and hotel lounges for a transformative sensory experience. Candles, incense, and diffusers come gift-boxed with our largest sizes to date - for luxury candle and fine goods lovers.",
    tags: ["Collection Overview", "Product Close-Up"],
  },
  {
    title: "The Alchemy Collection",
    thumbnail:
      "https://cdn.faire.com/fastly/ea82ea7197bd40d5d1402919acd9de5a6cfff3a34dbdb403c8c35f69717c2b35.jpeg?dpr=2&format=jpg&height=360",
    duration: "1 min",
    date: "Jul 10, 2026",
    description:
      "This collection of candles incense cones, and reed diffusers features science-backed blends meant to mimic the healing qualities of nature and boost your mood: Bergamot Shiso for vitality, Myrtle Mint for clarity, Geranium Moss for connection, and Enoki Cedar for grounding.",
    tags: ["Product Close-Up"],
  },
  {
    title: "Quality Fragrance Made for Your Atmosphere",
    thumbnail:
      "https://cdn.faire.com/fastly/4c12e3f8266e2629a549dc8b571a665cef7509711bc73fa3b92987fdf2fde258.png?dpr=2&format=jpg&height=360",
    duration: "1 min",
    date: "Jul 6, 2026",
    description:
      "P.F. Candle Co. makes high quality home fragrance for your atmosphere. While a lot has changed since we started in 2008, what remains the same is the care, quality, and real people behind the scenes we're known for. Video shot and produced by: HOPSVN",
    tags: ["Behind the Scenes", "Brand Story"],
  },
];

// Faire value props surfaced in the scrubbed (logged-out) state. [TODO confirm copy]
export const FAIRE_VALUE_PROPS: readonly string[] = [
  "Buyer protection on every order",
  "Flexible net-60 terms",
  "Free returns on your first order",
  "Reorder in one click",
];

// Faire platform value props — surfaced as the hero badges (top section) and as
// the benefit list inside the sign-up modal.
export const FAIRE_VALUE_BADGES: readonly string[] = [
  "60 days to pay",
  "Free returns on your first order",
  "New to Faire? 50% off + a year of free shipping",
];

// Olive "Retailers buy confidently on Faire" banner benefits — modeled on the
// production MoreProminenceBenefitsBanner (reference only; not imported).
export interface FaireBenefit {
  title: string;
  body: string;
}
export const FAIRE_BENEFITS: FaireBenefit[] = [
  {
    //   keeps "free shipping" clustered on one line when the title wraps.
    title: "50% off and 1 year of free shipping",
    body: "Claim our referral offer to get up to $150 off your first order with us and 1 year of free shipping.",
  },
  {
    title: "60-day payment terms on all orders",
    body: "When you order through Faire, use terms to buy now & pay 60 days later.",
  },
  {
    title: "Free returns on opening orders",
    body: "If something's not right, get a prepaid shipping label and send it back.",
  },
];

// Short benefit lines shown inside the sign-up modal.
export const SIGNUP_BENEFITS: string[] = [
  "See wholesale pricing",
  "Flexible net-60 terms",
  "Buyer protection on every order",
];

// Retailer social-proof testimonials (production copy; reference only).
export interface Testimonial {
  avatarUrl: string;
  quote: string;
  name: string;
  readStory?: boolean;
}
export const TESTIMONIALS: Testimonial[] = [
  {
    avatarUrl:
      "https://cdn.faire.com/fastly/f6385daef95d4fbf17bd60dcbcc9244008ae4dc7c91caa5ee5c338435c47548c.png",
    quote:
      "We're stocked in some of the best stores and art institutions in the world thanks to Faire, and use it to source products for our two shops in South East London too. Some of our strongest brand and supplier relationships have come through Faire.",
    name: "Jana Starcevic, co-founder of The Completist in London, UK",
    readStory: true,
  },
  {
    avatarUrl:
      "https://cdn.faire.com/fastly/808988fcf046cd1f0b85a2fff746b254c49eb72e32a4688b6ca322666319f2d3.jpeg",
    quote:
      "Faire allowed us to discover and source unique products for our own stores, all while simplifying the buying and selling process.",
    name: "Niko Dafkos, founder of Earl of East in London, UK",
    readStory: true,
  },
  {
    avatarUrl:
      "https://cdn.faire.com/fastly/d90320ac767e6018793a450e94b4c8ad4ec7964218ad8f091221709bc1f92fe1.jpeg",
    quote:
      "Faire makes it easy to find brands our customers love and reorder the ones that sell. The net terms are a game-changer for our cash flow.",
    name: "Priya Nair, owner of Marigold Home in Austin, TX",
  },
];

// Brand reviews (star summary + distribution + per-attribute + feed).
export interface AttributeScore {
  label: string;
  score: string;
}
export interface BrandReviewEntry {
  title: string;
  date: string;
  reviewer: string;
  location: string;
  reply?: { text: string; date: string };
}
export const REVIEW_SUMMARY = {
  rating: 4.9,
  count: 610,
  distribution: [
    { stars: 5, count: 588 },
    { stars: 4, count: 4 },
    { stars: 3, count: 5 },
    { stars: 2, count: 4 },
    { stars: 1, count: 2 },
  ],
  attributes: [
    { label: "Product quality", score: "5.0" },
    { label: "Fulfillment", score: "5.0" },
    { label: "Communication", score: "5.0" },
  ] as AttributeScore[],
};
export const BRAND_REVIEWS: BrandReviewEntry[] = [
  {
    title: "Love this candle brand so much!",
    date: "Jun 26, 2026",
    reviewer: "April",
    location: "Louisville, CO, United States",
    reply: {
      text: "Thank you, April! We love the Sunbeam team ☀️",
      date: "Jun 26, 2026",
    },
  },
  {
    title: "Excellent service & product!!",
    date: "Jun 25, 2026",
    reviewer: "Mya",
    location: "Wrangell, AK, United States",
    reply: {
      text: "That's what we aim for, Mya! Thanks so much for supporting P.F.!",
      date: "Jun 25, 2026",
    },
  },
  {
    title: "Stellar brand and AMAZING product. Definitely ordering again.",
    date: "Jun 22, 2026",
    reviewer: "Devon",
    location: "Portland, OR, United States",
  },
  {
    title: "Great smelling candles that burn exceptionally!",
    date: "Jun 18, 2026",
    reviewer: "Sam",
    location: "Austin, TX, United States",
    reply: {
      text: "We appreciate you, Sam — happy burning!",
      date: "Jun 19, 2026",
    },
  },
];

// Global-nav category links (the marketplace nav, matching the design).
export const NAV_LINKS = [
  { label: "Bestsellers", href: "#" },
  { label: "New", href: "#" },
  { label: "Home decor", href: "#" },
  { label: "Jewelry", href: "#" },
  { label: "Father's Day", href: "#" },
  { label: "Sale", href: "#", isSale: true },
];

// Keywords used to flag PF products as bestsellers (mock "performance" signal).
export const BESTSELLER_KEYWORDS: readonly string[] = [
  "teakwood & tobacco",
  "golden coast",
  "amber & moss",
  "piñon",
  "pinon",
  "sandalwood rose",
  "hinoki",
];

const PF_CATEGORY_TILES: CategoryTile[] = [
  { label: "Candles", keywords: ["candle"] },
  { label: "Incense", keywords: ["incense"] },
  { label: "Reed Diffusers", keywords: ["reed diffuser", "diffuser"] },
  { label: "Room and Linen Sprays", keywords: ["spray", "linen", "room"] },
  { label: "Air Fresheners", keywords: ["air freshener", "freshener"] },
  { label: "Accessories", keywords: ["trimmer", "wick", "match", "snuffer"] },
  // "At Home with Fragrance" is P.F. Candle Co.'s actual book product.
  { label: "Book", keywords: ["at home with fragrance", "book"] },
  // Parked tech — cutout "Shop all products" tile. To re-enable, swap the line
  // above for: { label: "Shop all products", keywords: ["book"], shopAll: true,
  //   image: "/images/pf-candle-co/shop-all-cutout.png" } (render branch lives in
  //   sections/CategoryTiles.tsx). FEEDBACK when revisiting: full-strength brand
  //   MAROON was too strong and overpowered the module — use a softer/lighter
  //   tone for the tile background.
];

// Map a product name (or collection) to its category label. Used as the
// referral signal that up-sorts the Faire all-products grid by what the shopper
// engaged with on brand.com. Returns undefined when nothing matches.
export const categoryLabelForProductName = (
  name: string
): string | undefined => {
  const lower = name.toLowerCase();
  const tile = PF_CATEGORY_TILES.find((categoryTile) =>
    categoryTile.keywords.some((keyword) => lower.includes(keyword))
  );
  return tile?.label;
};
export const categoryLabelForCollection = (label: string): string => {
  const lower = label.toLowerCase();
  if (lower.includes("diffuser") || lower.includes("flame-free")) {
    return "Reed Diffusers";
  }
  if (lower.includes("incense")) return "Incense";
  return "Candles";
};

const PF_COLLECTIONS: CollectionEntry[] = [
  {
    label: "SUMMER ARCHIVE CANDLES",
    imageUrl:
      "https://cdn.faire.com/fastly/d7ed86462a74d4897cc261d5835ab8b5544100017386c71be2936227b25e446b.jpeg?bg-color=FFFFFF&dpr=2&fit=crop&format=jpg&height=720&width=720",
  },
  {
    label: "*NEW* SALTWATER JASMINE",
    imageUrl:
      "https://cdn.faire.com/fastly/46028c1af95be788feb35fb9ccc2a83c09cbaba9c4b4eaa844afff535d648af7.jpeg?bg-color=FFFFFF&dpr=2&fit=crop&format=jpg&height=720&width=720",
  },
  {
    label: "Flame-free Fragrance",
    imageUrl:
      "https://cdn.faire.com/fastly/0abf71d3d9614d6e07dde02098ed0b30da989f4856cd3fc34c1fcb4a95f4a763.jpeg?bg-color=FFFFFF&dpr=2&fit=crop&format=jpg&height=720&width=720",
  },
  {
    label: "The HI-FI Collection",
    imageUrl:
      "https://cdn.faire.com/fastly/7b6ab0da6b88643edec4a4b6df5013d7a99732d4939713fc665ef72a034576c1.jpeg?bg-color=FFFFFF&dpr=2&fit=crop&format=jpg&height=720&width=720",
  },
];

const PF_PROMOS = ["Free shipping over $300", "Exclusivity available"];
const PF_PERFORMANCE_SIGNALS = ["Top 1% reordered in Candles & holders"];

const PF_ABOUT =
  "Hi, we're P.F. Candle Co. — an independently owned & operated home fragrance company based in sunny Los Angeles. As we grow, so does our commitment to sustainability, high-quality products, and to our crew who makes it all happen. We believe that fragrance is an extension of home design, and create products with accessible price points in mind. Our entire collection of 100% soy wax candles, room sprays, reed diffusers, incense, and air fresheners are vegan, cruelty-free, and phthalate-free — always.";

const PF_VALUE_TAGS = ["Handmade", "Eco-friendly", "Women owned"];

const PF_INFORMATION: { label: string; value: string }[] = [
  { label: "Established in", value: "2008" },
  { label: "Based in", value: "Los Angeles, California" },
  { label: "Ships from", value: "United States" },
  { label: "Products made in", value: "United States" },
];

// 1) Established — full P.F. Candle Co. data.
const ESTABLISHED: BrandDataset = {
  key: "established",
  name: "P.F. Candle Co.",
  logoSrc: "/images/pf-candle-co/logo-2025.svg",
  avatarUrl:
    "https://cdn.faire.com/fastly/d90320ac767e6018793a450e94b4c8ad4ec7964218ad8f091221709bc1f92fe1.jpeg",
  heroImage: HERO_COVER,
  coverImage: COVER_BANNER,
  // TYPEFACE — read off the real pfcandleco.com capture in
  // public/prototypes/pf-candle-co/pfcandle-wholesale.html, whose stack is
  // `GT-America, ... Jost, sans-serif`. GT America is a licensed Grilli Type
  // face with no webfont we can legitimately load, so this uses Jost — which is
  // not a substitute we picked, it is the fallback their own theme declares.
  brandFontFamily: '"Jost", system-ui, sans-serif',
  brandFontImportUrl:
    "https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500;600;700&display=swap",
  // Their real reviews, already imported. These three are the ones that speak
  // as a STOCKIST rather than a consumer ("our new storefront", "my hair
  // boutique") — which is the voice this surface needs, since its reader is
  // deciding whether to carry the line.
  featuredReviewTokens: ["review_pfc2", "review_pfc3", "review_pfc4"],
  brandSite: {
    announcement: "Wholesale on Faire — free shipping over $300",
    promoMessages: ["Net 60 terms", "Free returns on your first order"],
    socialHandle: "@pfcandleco",
    copyrightName: "P.F. Candle Co.",
    contactEmail: "wholesale@pfcandleco.com",
    contactAddress: "Los Angeles, CA",
    // Their real DTC nav/footer vocabulary, taken from the capture rather than
    // invented: Shop All / Shop By Scent / About Us / Contact Us / Wholesale FAQ.
    footerColumns: [
      { heading: "Shop", links: ["Shop All", "Shop By Scent", "Collections"] },
      { heading: "Company", links: ["About Us", "Contact Us", "Journal"] },
      {
        heading: "Wholesale",
        links: ["Wholesale FAQ", "Shipping Information", "Terms of Service"],
      },
    ],
  },
  brandColor: MAROON,
  brandColorDeep: MAROON_DEEP,
  secondaryColor: MAROON_SECONDARY,
  location: "Los Angeles, CA",
  minOrder: 250,
  rating: 4.9,
  reviewCount: 610,
  shipWindow: "Jul 7-16",
  aboutText: PF_ABOUT,
  promos: PF_PROMOS,
  performanceSignals: PF_PERFORMANCE_SIGNALS,
  categoryTiles: PF_CATEGORY_TILES,
  collections: PF_COLLECTIONS,
  hasBestsellers: true,
  hasCollections: true,
  hasPerformanceData: true,
  brandValueTags: PF_VALUE_TAGS,
  information: PF_INFORMATION,
  instagram: "@pfcandleco",
  website: "pfcandleco.com",
  videos: BRAND_VIDEOS,
  topCategorySubtitle: "Hand-poured soy candles in our signature scents.",
  bestsellersSubtitle: "These fragrances are our best sellers for a reason!",
  topCategoryHeroImage: CANDLES_LIFESTYLE_IMAGE,
  bestsellersHeroImage: PROMO_IMAGE,
  reviewDistribution: REVIEW_SUMMARY.distribution,
  seasonalCollection: {
    title: "Fall Classics",
    subtitle: "New fall-inspired scents, available for a limited time.",
    imageUrl: FALL_CLASSICS_IMAGE,
    productNames: [
      "Cardamom Bun – 7.2 oz Standard Candle",
      "Salted Mocha – 7.2 oz Standard Candle",
      "Spiced Pumpkin – 7.2 oz Standard Candle",
      "Apple Picking – 7.2 oz Standard Candle",
      "Spruce – 7.2 oz Standard Candle",
      "Cinnamon – 7.2 oz Standard Candle",
    ],
  },
  starterKitRangeCopy: "scents in candles, diffusers, and more",
  signaturePicks: ["teakwood", "amber & moss", "golden coast"],
  bestsellerNameHints: BESTSELLER_KEYWORDS,
  competitorKeywords: [
    "candle",
    "diffuser",
    "incense",
    "room spray",
    "fragrance",
    "wax",
    "perfume",
    "cologne",
    "soap",
  ],
  complementaryKeywords: [
    "vase",
    "mug",
    "bowl",
    "planter",
    "tea towel",
    "napkin",
    "tray",
    "coaster",
    "card",
    "notebook",
    "journal",
    "tea",
    "ceramic",
    "blanket",
    "throw",
    "dish",
    "tumbler",
    "cutting board",
    "linen",
    "pillow",
    "tote",
  ],
};

// 2) Mid — same brand, thinner merchandising: a couple of collections, still has
// bestsellers, but no "#N in category" performance ranking yet.
const MID: BrandDataset = {
  ...ESTABLISHED,
  key: "mid",
  reviewCount: 42,
  rating: 4.8,
  promos: ["Free shipping over $300", "New on Faire"],
  performanceSignals: [],
  collections: PF_COLLECTIONS.slice(0, 2),
  categoryTiles: PF_CATEGORY_TILES.slice(0, 5),
  hasPerformanceData: false,
};

// 3) New — the SAME brand (P.F. Candle Co.) presented as brand-new to Faire:
// full identity and imagery, but no Faire track record yet (no reviews → "New
// shop", no bestseller ranking), just a couple of collections and one intro
// video. Renders the minimal NewBrandHome storefront.
const NEW: BrandDataset = {
  ...ESTABLISHED,
  key: "new",
  rating: undefined,
  reviewCount: undefined,
  promos: ["Free shipping over $300", "New on Faire"],
  performanceSignals: [],
  collections: PF_COLLECTIONS.slice(0, 2),
  hasBestsellers: false,
  hasCollections: true,
  hasPerformanceData: false,
  // One intro video — renders as a single feature row (thumbnail + story).
  videos: BRAND_VIDEOS.slice(1, 2),
};

export const BRAND_DATASETS: Record<number, BrandDataset> = {
  1: ESTABLISHED,
  2: MID,
  3: NEW,
};

export const getBrandDataset = (brandVariant: number): BrandDataset =>
  BRAND_DATASETS[brandVariant] ?? ESTABLISHED;
